import { getGlobalLogger } from '@zg-rentals/logger-base';
import type { LogHttpRequest } from '@zg-rentals/monitor-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { makeAppInstance } from '@zg-rentals/util';
import { Maybe, result, type Result, toolbelt, Unit } from 'true-myth';

type ErrorContext = Record<string, unknown>;
const errorContext = makeAppInstance<ErrorContext>(() => ({}), '@zg-rentals/log-error:errorContext');

const handledErrors = makeAppInstance(() => new WeakSet<object>(), '@zg-rentals/log-error:handledErrors');
const suppressedErrors = makeAppInstance(() => new WeakSet<object>(), '@zg-rentals/log-error:suppressedErrors');

/**
 * Walks an error's `cause` chain looking for a match in `set`, so the same failure isn't
 * double-counted (or wrongly un-suppressed) as it's caught and re-thrown at multiple layers of the
 * stack. `ContextError` (and subclasses like `DatarouterError`) forward a `cause` for exactly this
 * reason: an HTTP client logs/suppresses the raw error, then re-wraps it into a domain error that a
 * React Query error handler logs again.
 */
function causeChainIntersects(set: WeakSet<object>, error: unknown): boolean {
  const seen = new Set<unknown>();
  let current = error;

  while (current !== null && typeof current === 'object' && !seen.has(current)) {
    if (set.has(current)) {
      return true;
    }

    seen.add(current);
    current = (current as { cause?: unknown }).cause;
  }

  return false;
}

/**
 * True if this error (or something it wraps) was already reported by an earlier `logError` call.
 *
 * Trade-off: the first layer to call `logError` for a given error wins outright. Whatever a later
 * layer would have reported for the same error - e.g. an HTTP client's request-level detail
 * reporting first, then a React Query error handler's queryKey/tags/variables never reporting at
 * all - is silently dropped, not merged. There's no batching point to combine them: each report is
 * a synchronous, fire-and-forget write to Splunk/Marlin/Dragnet at the moment `logError` is called,
 * with no later opportunity to enrich it. If a specific layer's context turns out to matter more
 * than whichever layer currently reports first, the fix is to have that layer call `logError`
 * before the others (or have the earlier layers suppress themselves in its favor), not to try to
 * merge after the fact.
 */
function isAlreadyHandled(error: unknown): boolean {
  return causeChainIntersects(handledErrors.get(), error);
}

/** True if this error (or something it wraps) was explicitly suppressed via {@link suppressErrorLogging}. */
function isSuppressed(error: unknown): boolean {
  return causeChainIntersects(suppressedErrors.get(), error);
}

function markHandled(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    handledErrors.get().add(error);
  }
}

/**
 * Marks an error (and anything that later wraps it via `cause`) as suppressed, so a subsequent
 * `logError` call for the same error is a no-op - even one passing `forceEmit`. Useful when a
 * caller has its own reason to suppress reporting an error (e.g. a PerimeterX-blocked HTTP
 * response) but the error still bubbles up to a layer that would otherwise log it.
 */
export const suppressErrorLogging = (error: unknown) => {
  if (error !== null && typeof error === 'object') {
    suppressedErrors.get().add(error);
  }
};

/**
 * Clears the sets of errors tracked as "already handled" and "suppressed". Intended for test
 * isolation between cases that reuse the same error instance; production code shouldn't need this.
 */
export const resetHandledErrors = () => {
  handledErrors.set(new WeakSet<object>());
  suppressedErrors.set(new WeakSet<object>());
};
export const MAX_STACK_TRACE_LINES = 15;
export const MAX_STACK_TRACE_LENGTH = 1_500;
export const ERROR_BOUNDARY_PREFIX = 'ErrorBoundary';

type LogErrorOptions = {
  error: Error | unknown;
  context?: string | Record<string, unknown>;
  errorType?: string;
  httpRequest?: LogHttpRequest;
  /**
   * Makes this call always report, even if an earlier `logError` call already reported this error
   * (or something it wraps via `cause`) - e.g. an HTTP client that already logged the raw error,
   * later re-thrown into a component tree and caught by an error boundary. Useful for a reporter of
   * last resort - e.g. an error boundary - that must log regardless of what ran before it.
   *
   * Does *not* override an explicit {@link suppressErrorLogging} call - a deliberate suppression
   * (e.g. PerimeterX-blocked noise) always wins, even over `forceEmit`.
   */
  forceEmit?: boolean;
};

export type LogErrorBeforeSendEvent = {
  error: Error | unknown;
  errorType: string;
  message: string;
  context: ErrorContext;
  httpRequest?: LogHttpRequest;
};

export type LogErrorBeforeSend = (event: LogErrorBeforeSendEvent) => boolean | void;

type PreparedLogError = {
  error: Error | unknown;
  errorType: string;
  fullContext: ErrorContext;
  httpRequest?: LogHttpRequest;
  message: string;
  prefixedErrorType: string;
  totalErrorName: string;
};

/**
 * Adds global error context that will be logged with all other error information to Splunk.
 * Local context passed to `logError` will take precedence and be spread _after_ the global context
 */
export const setGlobalErrorContext = (ctx: ErrorContext) => errorContext.set({ ...errorContext.get(), ...ctx });

const enabled = makeAppInstance(() => true, '@zg-rentals/log-error:enabled');
const logErrorBeforeSend = makeAppInstance<LogErrorBeforeSend | undefined>(
  () => undefined,
  '@zg-rentals/log-error:logErrorBeforeSend',
);

/**
 * An escape hatch to disable log error, perhaps if you intend to redirect logged
 * out users and have a bunch of pending promises/network requests that will be
 * terminated by redirecting.
 */
export const disableLogError = () => enabled.set(false);

/**
 * Adds a callback that can inspect prepared error data before any reporters run.
 * Return `false` to discard the error.
 */
export const setLogErrorBeforeSend = (beforeSend?: LogErrorBeforeSend) => logErrorBeforeSend.set(beforeSend);

export type { LogHttpRequest };

/**
 * Throw this error to attach context to the error that can be consumed higher up
 * in the stack. In general this error type should be used only in module code to
 * propagate error context out to apps that call `logError`.
 *
 * Pass `cause` when this error replaces one caught from a lower layer (e.g. an `HTTPError`), so
 * `logError`'s dedup/suppression tracking can still find it by walking the `cause` chain.
 */
export class ContextError extends Error {
  constructor(
    name: string,
    public readonly context: Record<string, unknown>,
    public readonly cause?: unknown,
  ) {
    // Assigned as a plain field rather than via `super(name, { cause })` - some consumers'
    // tsconfigs (e.g. cypress apps, see modules/tsconfig/cypress.json) don't include the
    // `es2022.error` lib, which is where that Error constructor overload is declared.
    super(name);
    this.name = name;
  }

  public static is(error: unknown): error is ContextError {
    return error instanceof ContextError;
  }
}

function truncateStackTrace(stack: Maybe<string>) {
  return stack.map((contents) => {
    let splitLines = contents.split('\n');
    if (splitLines.length > MAX_STACK_TRACE_LINES) {
      splitLines = splitLines.slice(0, MAX_STACK_TRACE_LINES).concat('...');
    }

    const newStack = splitLines.join('\n');
    return newStack.length > MAX_STACK_TRACE_LENGTH ? newStack.slice(0, MAX_STACK_TRACE_LENGTH) + '...' : newStack;
  });
}

const getEnvironmentContext = () => {
  const isServer = typeof window === 'undefined';

  return {
    errorPrefix: `${isServer ? 'httpServer' : 'browser'} error`,
    fullContext: isServer
      ? {}
      : {
          originPath: window.location.origin + window.location.pathname,
          userAgent: window.navigator.userAgent,
        },
  };
};

const prepareLogError = ({
  error,
  context,
  errorType = error instanceof Error ? error.name : 'UnknownError',
  httpRequest,
}: LogErrorOptions): Result<PreparedLogError, string> =>
  result.tryOrElse(
    (reason) => `prepareLogError: ${reason}`,
    () => {
      const isErrorInstance = error instanceof Error;
      const { errorPrefix, fullContext: environmentContext } = getEnvironmentContext();

      const localContext = typeof context === 'string' ? { context } : (context ?? {});
      const contextErrorContext = ContextError.is(error) ? error.context : {};

      // Truncate stack trace so Splunk logs don't lose formatting
      const stack = truncateStackTrace(Maybe.of((isErrorInstance ? error : new Error()).stack)).unwrapOr(
        '[no stack trace provided]',
      );

      if (isErrorInstance) {
        // Ensure monitor?.error doesn't receive lengthy/circular stack traces.
        error.stack = stack;
      }

      const fullContext = {
        stack,
        ...errorContext.get(),
        ...contextErrorContext,
        ...localContext,
        ...environmentContext,
      };

      // Serialize error into a readable string, useful for Splunk logs.
      const message = isErrorInstance ? error.message : `Serialized error: ${JSON.stringify(error)}`;
      errorType = errorType.replace(/\s/g, '');

      return {
        error,
        errorType,
        fullContext,
        httpRequest,
        message,
        prefixedErrorType: `${errorPrefix} type ${errorType}`,
        totalErrorName: `${errorPrefix} total`,
      };
    },
  );

const reportToSplunk = ({ fullContext, message, prefixedErrorType }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `getGlobalLogger(${reason})`,
      () => Maybe.of(getGlobalLogger(prefixedErrorType)),
    )
    .andThen((logger) => toolbelt.fromMaybe('reportToSplunk: logger is undefined', logger))
    .andThen((logger) =>
      result.tryOrElse(
        (reason) => `logger.error(${reason})`,
        () => logger.error(fullContext, `log-error reporting: ${message}`),
      ),
    )
    .map(() => Unit)
    .mapErr((reason) => `reportToSplunk: ${reason}`);

const reportToMarlin = ({ prefixedErrorType, totalErrorName }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `reportToMarlin: ${reason}`,
      () => {
        const monitor = getGlobalMonitor();
        monitor.count({ name: totalErrorName });
        monitor.count({ name: prefixedErrorType });
      },
    )
    .map(() => Unit);

const reportToDragnet = ({ error, errorType, fullContext, httpRequest }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `reportToDragnet: ${reason}`,
      () => {
        if (error instanceof Error) {
          getGlobalMonitor().error({
            error,
            httpRequest,
            isErrorBoundary: errorType.startsWith(ERROR_BOUNDARY_PREFIX),
            errorType,
            context: fullContext,
          });
        }
      },
    )
    .map(() => Unit);

const runLogErrorBeforeSend = ({
  error,
  errorType,
  fullContext,
  httpRequest,
  message,
}: PreparedLogError): Result<boolean, string> => {
  const beforeSend = logErrorBeforeSend.get();

  if (!beforeSend) {
    return result.ok(true);
  }

  return result.tryOrElse(
    (reason) => `logErrorBeforeSend: ${reason}`,
    () =>
      beforeSend({
        error,
        errorType,
        message,
        context: fullContext,
        httpRequest,
      }) !== false,
  );
};

/**
 * Call this function to log errors in the Rental Platform standardized way. Assuming
 * your app is configured with a Bootstrap instance, your errors should show up in some
 * combination of Splunk, Marlin, and Dragnet (based on bootstrap configuration).
 */
export function logError({
  error,
  context,
  errorType = error instanceof Error ? error.name : 'UnknownError',
  httpRequest,
  forceEmit = false,
}: LogErrorOptions): Result<Unit, Array<string>> {
  if (!enabled.get() || isSuppressed(error) || (!forceEmit && isAlreadyHandled(error))) {
    return result.ok(Unit);
  }

  return prepareLogError({ error, context, errorType, httpRequest })
    .mapErr((reason) => [reason])
    .andThen((prepared) => {
      const beforeSendResult = runLogErrorBeforeSend(prepared);

      // A `beforeSend` that explicitly returns `false` is an authoritative decision not to send;
      // one that throws is treated like `true` - reporting still goes ahead below, with the
      // failure folded into the final result instead of stopping it.
      if (beforeSendResult.mapOr(false, (shouldSend) => !shouldSend)) {
        markHandled(error);
        return result.ok(Unit);
      }

      const reportResults = [reportToSplunk(prepared), reportToMarlin(prepared), reportToDragnet(prepared)];

      // Mark handled once at least one reporter actually sent something, so a later call for the
      // same error is deduped. If every reporter failed, leave it unhandled so a subsequent call
      // still gets a chance to report, instead of silently losing it forever.
      if (result.transposeAny(reportResults).isOk) {
        markHandled(error);
      }

      return result.transposeAll([beforeSendResult, ...reportResults]).map(() => Unit);
    })
    .inspectErr(
      // eslint-disable-next-line no-console
      (reasons) => console.error(`logError failed`, { reasons }),
    );
}
